import {getEnvironmentByHost} from './getEnvironmentByHost'

const SESSION_REQ_KEY = '__sessionRequestId'

type StaticValues = {
  env: 'testing' | 'production' | 'development'
  sessionId: string
  requestId: string
  parentRequestId?: string
  platformType: 'desktop' | 'app'
  platformVendor: 'non_native' | 'ios' | 'android'
}

/**
 * Перед тем как добавить сюда что то и начать отправлять это что то во всех логах - надо осознать зачем это делается.
 * Не стоит сюда класть данные которые можно отправить один раз для всей сессии, например не стоит сюда отправлять локальное время девайса.
 * Потому что это информация об окружении которая не понадобится для группировки логов по конкретному значению - ее можно отправить отдельным событием.
 */
type DynamicValues = {
  service: string
  version: string
  partnerId?: string
}

type SessionStoreValues = StaticValues & DynamicValues

type Subscriber = () => void
type Unsubscribe = () => void

class SessionStore {
  private store: SessionStoreValues
  private subs: Set<Subscriber> = new Set()

  constructor() {
    const {platformType, platformVendor} = this.getNativePlatformInfo()

    this.store = {
      service: 'empty_service_name',
      version: 'empty_version',
      env: getEnvironmentByHost(),
      sessionId: this.getSessionId(),
      requestId: this.getRequestId(),
      parentRequestId: this.getParentRequestId(),
      platformType,
      platformVendor
    }
  }

  get data() {
    return this.store
  }

  onDataChange(sub: Subscriber): Unsubscribe {
    this.subs.add(sub)
    return () => void this.subs.delete(sub)
  }

  setData(value: Partial<DynamicValues>) {
    Object.assign(this.store, value)
    this.subs.forEach((sub) => sub())
  }

  /** ID сессии - хранится в localStorage поэтому общий для всех вкладок или iframe */
  private getSessionId() {
    try {
      const SESSION_ID_KEY = '__sessionId'
      const current = localStorage.getItem(SESSION_ID_KEY)

      if (current) {
        return current
      }

      const newId = `${Date.now()}.${(Math.random() * 1_000_000_000).toFixed(0)}`

      localStorage.setItem(SESSION_ID_KEY, newId)

      return newId
    } catch (error) {
      console.error(error)
      return 'unable_to_get_session_id'
    }
  }

  /** ID внутренней сессии - хранится в window поэтому разный в разных вкладках или iframe */
  private getRequestId() {
    if (SESSION_REQ_KEY in window && typeof window[SESSION_REQ_KEY] === 'string') {
      return window[SESSION_REQ_KEY]
    }

    const reqId = `${Date.now()}.${(Math.random() * 1_000_000_000).toFixed(0)}`

    Object.assign(window, {[SESSION_REQ_KEY]: reqId})

    return reqId
  }

  /** ID внутренней сессии родителя - возвращается только если iframe проинициализирован в коре */
  private getParentRequestId() {
    try {
      if (window.parent === window) {
        return
      }

      if (SESSION_REQ_KEY in window.parent && typeof window.parent[SESSION_REQ_KEY] === 'string') {
        return window.parent[SESSION_REQ_KEY]
      }

      return 'unknown_parent_req_id'
    } catch (error) {
      console.error(error)
    }
  }

  private getNativePlatformInfo() {
    let iosApp = false
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      iosApp = Boolean((window.parent as any).webkit?.messageHandlers?.restappNative)
    } catch {
      // noop
    }

    let androidApp = false
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      androidApp = Boolean((window.parent as any).AndroidInterface)
    } catch {
      // noop
    }

    let platformType: 'desktop' | 'app' = 'desktop'
    if (iosApp || androidApp) platformType = 'app'

    let platformVendor: 'non_native' | 'ios' | 'android' = 'non_native'
    if (iosApp) platformVendor = 'ios'
    if (androidApp) platformVendor = 'android'

    return {
      platformType,
      platformVendor
    }
  }
}

export const sessionStore = new SessionStore()
